import { action, computed, makeObservable, observable } from 'mobx';
import { parse } from 'qs';
import { RouterStore } from '@superwf/mobx-react-router';
import { langFromPath, pathStartsWithLang, pathWithoutLang, toPath, toPathname } from '@util/NavigationUtil';
import RootStore from './RootStore';
import {ALLOWED_LANGUAGES, DEFAULT_LANGUAGE, Language, languageSlug} from './LanguageStore'

export const SEGMENT_PRIVATE = 'era';
export const SEGMENT_BUSINESS = 'ari';

class RouteStore {
  @observable public showingSpinner: boolean = false;
  @observable public previousRoute: string = '';
  private routing: RouterStore;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.routing = rootStore.routing;
  }

  private get location() {
    return this.routing.location;
  }

  @computed
  get pathname() {
    return this.location.pathname || '/';
  }

  @computed
  get path() {
    return toPath(this.pathname);
  }

  get langFromCurrentPath(): Language {
    return langFromPath(this.path);
  }

  @computed
  get params() {
    return this.location.search;
  }

  @computed
  get decodedParams() {
    return parse(this.params, { ignoreQueryPrefix: true });
  }

  @computed
  get segment() {
    return this.path[this.pathIncludesLang() ? 1 : 0] || SEGMENT_PRIVATE;
  }

  @action
  public navigate = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    const url = event.currentTarget?.href;
    if (url) {
      return window.location.assign(url);
    }
  };

  public pathIncludesLang = (path: string[] = this.path) => {
    return path && path.length > 0 && ['et', 'ru'].includes(path[0]);
  };

  public langFromPath() {
    return this.pathIncludesLang() ? this.path[0] : null;
  }

  @computed
  private get pathnameWithoutLang() {
    return toPathname(this.pathWithoutLang);
  }

  @computed
  get pathWithoutLang(): string[] {
    return pathWithoutLang(this.path);
  }

  @action
  public setLanguage = (requiredLang: Language) => {
    if (!ALLOWED_LANGUAGES.includes(requiredLang)) {
      throw new Error(`Unsupported language: ${requiredLang}`);
    }

    const currentLang = this.langFromCurrentPath;
    const currentPathMatchesLang = (requiredLang === DEFAULT_LANGUAGE)
        ? !pathStartsWithLang(this.path)
        : (currentLang === requiredLang);

    if (!currentPathMatchesLang) {
      this.routing.replace(languageSlug(requiredLang) + this.pathnameWithoutLang + location.search + location.hash);
    }
  };

  @action
  public replace(url: string, replaceExternals: boolean = false) {
    const htmlLink = document.createElement('a');
    htmlLink.href = url;
    if (htmlLink.host === window.location.host) {
      this.routing.replace(htmlLink.pathname + htmlLink.search + htmlLink.hash);
    } else {
      replaceExternals ? window.location.replace(url) : window.location.assign(url);
    }
  }
}

export default RouteStore;
